import { PayloadAction } from '@reduxjs/toolkit';
import createAsyncSlice from '../createAsyncSlice';
import { User } from '../../types';
import { gameEvent } from '../../services/userService';

/**
 * Represents the state of the game parrot game.
 */
export interface GameParrotSlice {
  opponent: User | undefined;
  gameStatus: (string | number | number[][])[][];
  gameResult: (string | number[])[];
  battleshipHits: number[][];
  challenged: User | undefined;
  challengeModalOpen: boolean;
  ticTacToeModalOpen: boolean;
  connectFourModalOpen: boolean;
  battleshipModalOpen: boolean;
  status: 'idle' | 'loading' | 'failed';
}

/**
 * Represents the initial state of the game parrot game.
 */
const initialState: GameParrotSlice = {
  opponent: undefined,
  gameStatus: [],
  gameResult: ['incomplete'],
  battleshipHits: [],
  challenged: undefined,
  challengeModalOpen: false,
  ticTacToeModalOpen: false,
  connectFourModalOpen: false,
  battleshipModalOpen: false,
  status: 'idle',
};

/**
 * Creates reducers and selectors with which to used to interact with the game parrot game.
 */
export const gameParrotSlice = createAsyncSlice({
  name: 'gameParrotSlice',
  initialState,
  reducers: create => ({
    // Sends a backend request to start, take a turn in, or quit a game, and
    // updates the state with the result
    playGame: create.asyncThunk(
      async (data: {
        going: string;
        waiting: string;
        data: (string | number)[];
        type: 'initiation' | 'turn' | 'quit' | 'exit';
      }) => {
        const { gameStatus, result } = await gameEvent(
          data.going,
          data.waiting,
          data.data,
          data.type,
        );
        return [gameStatus, result];
      },
      {
        pending: state => {
          state.status = 'loading';
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          [state.gameStatus, state.gameResult] = action.payload;
          const hit = state.gameResult.length === 2 ? (state.gameResult[1] as number[]) : [];
          if (hit[0] !== -1) state.battleshipHits.push(hit);
        },
        rejected: (state, action) => {
          state.status = 'failed';
        },
      },
    ),
    // Update's the state's opponent with the given ModelType
    setOpponent: create.reducer((state, action: PayloadAction<User | undefined>) => {
      state.opponent = action.payload;
    }),
    setGameStatus: create.reducer((state, action: PayloadAction<(string | number)[][]>) => {
      state.gameStatus = action.payload;
    }),
    setGameResult: create.reducer((state, action: PayloadAction<(string | number[])[]>) => {
      state.gameResult = action.payload;
    }),
    setBattleshipHits: create.reducer((state, action: PayloadAction<number[][]>) => {
      state.battleshipHits = action.payload;
    }),
    setChallenged: create.reducer((state, action: PayloadAction<User>) => {
      state.challenged = action.payload;
    }),
    setChallengeModalOpen: create.reducer((state, action: PayloadAction<boolean>) => {
      state.challengeModalOpen = action.payload;
    }),
    setTicTacToeModalOpen: create.reducer((state, action: PayloadAction<boolean>) => {
      state.ticTacToeModalOpen = action.payload;
    }),
    setConnectFourModalOpen: create.reducer((state, action: PayloadAction<boolean>) => {
      state.connectFourModalOpen = action.payload;
    }),
    setBattleshipModalOpen: create.reducer((state, action: PayloadAction<boolean>) => {
      state.battleshipModalOpen = action.payload;
    }),
  }),
  selectors: {
    opponentSelector: state => state.opponent,
    gameStatusSelector: state => state.gameStatus,
    gameResultSelector: state => state.gameResult,
    battleshipHitsSelector: state => state.battleshipHits,
    challengedSelector: state => state.challenged,
    challengeModalOpenSelector: state => state.challengeModalOpen,
    ticTacToeModalOpenSelector: state => state.ticTacToeModalOpen,
    connectFourModalOpenSelector: state => state.connectFourModalOpen,
    battleshipModalOpenSelector: state => state.battleshipModalOpen,
  },
});

export const {
  playGame,
  setOpponent,
  setGameStatus,
  setGameResult,
  setBattleshipHits,
  setChallenged,
  setChallengeModalOpen,
  setTicTacToeModalOpen,
  setConnectFourModalOpen,
  setBattleshipModalOpen,
} = gameParrotSlice.actions;

export const {
  opponentSelector,
  gameStatusSelector,
  gameResultSelector,
  battleshipHitsSelector,
  challengedSelector,
  challengeModalOpenSelector,
  ticTacToeModalOpenSelector,
  connectFourModalOpenSelector,
  battleshipModalOpenSelector,
} = gameParrotSlice.selectors;
